/*
Copyright 2026 Chain Care.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import type { CallMembershipIdentityParts } from "matrix-js-sdk/lib/matrixrtc/EncryptionManager";

export const PRENIGMA_MATRIX_RTC_AGENT_JOIN_CONTEXT_SCHEMA =
  "prenigma.element_call.matrix_rtc_agent_join_context.v1" as const;

export type PrenigmaMatrixRtcAgentJoinContextStatus = "blocked" | "recorded";

export interface PrenigmaMatrixRtcAgentJoinContext {
  deviceId: string;
  matrixRoomId: string;
  matrixUserId: string;
  memberId: string;
  observedAtMs: number;
  rawAudioReturned: false;
  rawCredentialsReturned: false;
  rawSecretMaterialReturned: false;
  rawTokenReturned: false;
  rawTranscriptReturned: false;
  schema: typeof PRENIGMA_MATRIX_RTC_AGENT_JOIN_CONTEXT_SCHEMA;
}

export interface PrenigmaMatrixRtcAgentJoinContextEvidence {
  blockers: string[];
  claimedDeviceIdPresent: boolean;
  claimedUserIdPresent: boolean;
  matrixRoomIdPresent: boolean;
  memberIdPresent: boolean;
  observedAtMs: number;
  rawAudioReturned: false;
  rawCredentialsReturned: false;
  rawSecretMaterialReturned: false;
  rawTokenReturned: false;
  rawTranscriptReturned: false;
  schema: typeof PRENIGMA_MATRIX_RTC_AGENT_JOIN_CONTEXT_SCHEMA;
  status: PrenigmaMatrixRtcAgentJoinContextStatus;
}

export interface PrenigmaMatrixRtcAgentJoinContextHost {
  acknowledgeAgentJoinContext?: (
    evidence: PrenigmaMatrixRtcAgentJoinContextEvidence,
  ) => void;
  readLastAgentJoinContext?: () =>
    | PrenigmaMatrixRtcAgentJoinContext
    | undefined;
  recordAgentJoinContext?: (context: PrenigmaMatrixRtcAgentJoinContext) => void;
}

export interface RecordPrenigmaMatrixRtcAgentJoinContextOptions {
  host?: PrenigmaMatrixRtcAgentJoinContextHost;
  membership: CallMembershipIdentityParts;
  observedAtMs?: number;
  roomId: string;
}

export interface RecordPrenigmaMatrixRtcAgentJoinContextResult {
  context?: PrenigmaMatrixRtcAgentJoinContext;
  evidence: PrenigmaMatrixRtcAgentJoinContextEvidence;
}

declare global {
  interface Window {
    prenigmaMatrixRtcAgentJoinContext?: PrenigmaMatrixRtcAgentJoinContextHost;
    prenigmaMatrixRtcAgentJoinContextLast?: PrenigmaMatrixRtcAgentJoinContext;
  }
}

const RAW_MATERIAL_KEYS = new Set([
  "access_token",
  "api_key",
  "api_secret",
  "audio",
  "audio_base64",
  "audio_bytes",
  "authorization",
  "bearer",
  "client_secret",
  "credentials",
  "id_token",
  "jwt",
  "livekit_token",
  "matrix_event_content",
  "openid_token",
  "password",
  "private_key",
  "provider_api_key",
  "provider_secret",
  "raw_audio",
  "raw_event_content",
  "raw_secret_material",
  "raw_transcript",
  "refresh_token",
  "secret",
  "secret_key",
  "source_event_content",
  "token",
  "transcript",
  "transcript_text",
]);

export function getPrenigmaMatrixRtcAgentJoinContextHost():
  | PrenigmaMatrixRtcAgentJoinContextHost
  | undefined {
  if (typeof window === "undefined") return undefined;
  return window.prenigmaMatrixRtcAgentJoinContext;
}

export function recordPrenigmaMatrixRtcAgentJoinContext({
  host = getPrenigmaMatrixRtcAgentJoinContextHost(),
  membership,
  observedAtMs = Date.now(),
  roomId,
}: RecordPrenigmaMatrixRtcAgentJoinContextOptions): RecordPrenigmaMatrixRtcAgentJoinContextResult {
  const rawMaterialDetected = containsRawMaterial({
    membership,
    roomId,
  });
  const fieldPresence = {
    claimedDeviceIdPresent: textValue(membership.deviceId).length > 0,
    claimedUserIdPresent: textValue(membership.userId).length > 0,
    matrixRoomIdPresent: textValue(roomId).length > 0,
    memberIdPresent: textValue(membership.memberId).length > 0,
  };
  const blockers = missingFieldBlockers(fieldPresence);

  if (rawMaterialDetected) {
    blockers.unshift("raw_material_key_present");
  }

  if (blockers.length > 0) {
    const evidence = buildEvidence({
      blockers,
      fieldPresence,
      observedAtMs,
      status: "blocked",
    });
    notifyEvidence(host, evidence);
    return { evidence };
  }

  const context: PrenigmaMatrixRtcAgentJoinContext = {
    deviceId: textValue(membership.deviceId),
    matrixRoomId: textValue(roomId),
    matrixUserId: textValue(membership.userId),
    memberId: textValue(membership.memberId),
    observedAtMs,
    rawAudioReturned: false,
    rawCredentialsReturned: false,
    rawSecretMaterialReturned: false,
    rawTokenReturned: false,
    rawTranscriptReturned: false,
    schema: PRENIGMA_MATRIX_RTC_AGENT_JOIN_CONTEXT_SCHEMA,
  };
  const evidence = buildEvidence({
    blockers: [],
    fieldPresence,
    observedAtMs,
    status: "recorded",
  });

  if (typeof window !== "undefined") {
    window.prenigmaMatrixRtcAgentJoinContextLast = context;
  }
  notifyContext(host, context);
  notifyEvidence(host, evidence);
  return { context, evidence };
}

function buildEvidence({
  blockers,
  fieldPresence,
  observedAtMs,
  status,
}: {
  blockers: string[];
  fieldPresence: Pick<
    PrenigmaMatrixRtcAgentJoinContextEvidence,
    | "claimedDeviceIdPresent"
    | "claimedUserIdPresent"
    | "matrixRoomIdPresent"
    | "memberIdPresent"
  >;
  observedAtMs: number;
  status: PrenigmaMatrixRtcAgentJoinContextStatus;
}): PrenigmaMatrixRtcAgentJoinContextEvidence {
  return {
    ...fieldPresence,
    blockers: [...new Set(blockers)],
    observedAtMs,
    rawAudioReturned: false,
    rawCredentialsReturned: false,
    rawSecretMaterialReturned: false,
    rawTokenReturned: false,
    rawTranscriptReturned: false,
    schema: PRENIGMA_MATRIX_RTC_AGENT_JOIN_CONTEXT_SCHEMA,
    status,
  };
}

function missingFieldBlockers(
  fieldPresence: Pick<
    PrenigmaMatrixRtcAgentJoinContextEvidence,
    | "claimedDeviceIdPresent"
    | "claimedUserIdPresent"
    | "matrixRoomIdPresent"
    | "memberIdPresent"
  >,
): string[] {
  const blockers: string[] = [];
  if (!fieldPresence.matrixRoomIdPresent) {
    blockers.push("matrix_room_id_missing");
  }
  if (!fieldPresence.memberIdPresent) blockers.push("member_id_missing");
  if (!fieldPresence.claimedUserIdPresent) {
    blockers.push("claimed_user_id_missing");
  }
  if (!fieldPresence.claimedDeviceIdPresent) {
    blockers.push("claimed_device_id_missing");
  }
  return blockers;
}

function notifyContext(
  host: PrenigmaMatrixRtcAgentJoinContextHost | undefined,
  context: PrenigmaMatrixRtcAgentJoinContext,
): void {
  try {
    host?.recordAgentJoinContext?.(context);
  } catch {
    // The call must not fail when a test harness observer is unavailable.
  }
}

function notifyEvidence(
  host: PrenigmaMatrixRtcAgentJoinContextHost | undefined,
  evidence: PrenigmaMatrixRtcAgentJoinContextEvidence,
): void {
  try {
    host?.acknowledgeAgentJoinContext?.(evidence);
  } catch {
    // The call must not fail when a test harness observer is unavailable.
  }
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent("prenigma:matrix-rtc-agent-join-context", {
        detail: evidence,
      }),
    );
  } catch {
    // Dispatch is best-effort diagnostics only.
  }
}

function containsRawMaterial(value: unknown, depth = 0): boolean {
  if (value === null || value === undefined || depth > 6) return false;
  if (Array.isArray(value)) {
    return value.some((item) => containsRawMaterial(item, depth + 1));
  }
  if (typeof value !== "object") return false;
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (RAW_MATERIAL_KEYS.has(normalizeKey(key))) return true;
    if (containsRawMaterial(item, depth + 1)) return true;
  }
  return false;
}

function normalizeKey(key: string): string {
  return key
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[-\s]+/g, "_")
    .toLowerCase();
}

function textValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
